import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

interface SuperconductorType {
  name: string;
  characteristic: string;
  example: string;
  icon: string;
  color: string;
}

interface KeyPhenomenon {
  name: string;
  description: string;
  icon: string;
}

interface Application {
  name: string;
  detail: string;
  status: string;
  icon: string;
}

interface SuperconductorFact {
  label: string;
  icon: string;
}

interface SuperconductorTemplateProps {
  topic: string;
  subtitle: string;
  superconductorTypes: SuperconductorType[];
  keyPhenomena: KeyPhenomenon[];
  applications: Application[];
  scFacts: SuperconductorFact[];
  criticalTempData: { material: string; temp: string; year: string }[];
  stats: { value: string; label: string }[];
  bottomQuote: string;
}

const DEFAULT_PROPS: SuperconductorTemplateProps = {
  topic: '超导体与量子锁定',
  subtitle: 'Superconductors & Quantum Locking',
  superconductorTypes: [
    {
      name: 'I型超导体',
      characteristic: '完全迈斯纳效应，低临界磁场',
      example: '铅(Pb)、锡(Sn)、汞(Hg)',
      icon: '🔷',
      color: '#0284c7',
    },
    {
      name: 'II型超导体',
      characteristic: '混合态允许部分磁通穿透',
      example: '铌钛(NbTi)、YBCO',
      icon: '🔶',
      color: '#38bdf8',
    },
    {
      name: '高温超导体',
      characteristic: '临界温度高于液氮温度(77K)',
      example: 'YBCO(93K)、BSCCO(110K)',
      icon: '🔥',
      color: '#7dd3fc',
    },
    {
      name: '室温候选超导体',
      characteristic: '超高压或新材料路径',
      example: 'LaH₁₀(250K/170GPa)',
      icon: '🌡️',
      color: '#bae6fd',
    },
  ],
  keyPhenomena: [
    {
      name: '零电阻',
      description: '电流无损耗流动，永久电流',
      icon: '⚡',
    },
    {
      name: '迈斯纳效应',
      description: '完全排斥内部磁场',
      icon: '🧲',
    },
    {
      name: '量子锁定',
      description: '磁通钉扎使超导体悬浮固定',
      icon: '🔒',
    },
    {
      name: '磁通量子化',
      description: '磁通以Φ₀为单位量子化穿透',
      icon: '🌀',
    },
  ],
  applications: [
    {
      name: '磁悬浮列车',
      detail: '利用迈斯纳效应实现无摩擦运输',
      status: '已商用',
      icon: '🚄',
    },
    {
      name: '粒子加速器',
      detail: '超导磁体产生强偏转磁场',
      status: 'LHC运行中',
      icon: '⚛️',
    },
    {
      name: 'MRI医学成像',
      detail: '超导线圈产生均匀强磁场',
      status: '全球普及',
      icon: '🏥',
    },
    {
      name: '量子计算',
      detail: '超导量子比特实现量子逻辑门',
      status: '快速发展中',
      icon: '💻',
    },
  ],
  scFacts: [
    { label: 'BCS理论', icon: '📐' },
    { label: '库珀对', icon: '👥' },
    { label: '约瑟夫森效应', icon: '🌊' },
    { label: '能隙', icon: '🔲' },
  ],
  criticalTempData: [
    { material: '汞(Hg)', temp: '4.2K', year: '1911' },
    { material: 'Nb₃Ge', temp: '23.2K', year: '1973' },
    { material: 'YBCO', temp: '93K', year: '1987' },
    { material: 'HgBaCaCuO', temp: '133K', year: '1993' },
    { material: 'LaH₁₀', temp: '250K', year: '2019' },
  ],
  stats: [
    { value: '-269°C', label: '首个超导体临界温度(汞)' },
    { value: '250K', label: '最高临界温度记录' },
    { value: '1911', label: '超导现象发现年份' },
  ],
  bottomQuote:
    '室温超导是凝聚态物理的圣杯——一旦实现，将彻底改变能源传输、交通运输和计算技术的面貌',
};

export const SuperconductorTemplate: React.FC<
  Partial<SuperconductorTemplateProps>
> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const headerIn = spring({
    frame,
    fps,
    config: { damping: 18, stiffness: 80 },
  });

  const typeStart = 30;
  const phenomenonStart = 160;
  const svgStart = 200;
  const appStart = 300;
  const factStart = 380;
  const statsStart = 440;
  const quoteStart = 500;

  const quoteOpacity = interpolate(frame, [quoteStart, quoteStart + 30], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const meissnerDraw = interpolate(
    frame,
    [svgStart, svgStart + 100],
    [0, 1],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
  );

  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(180deg, #0a1e3d 0%, #051225 50%, #020810 100%)',
        fontFamily: '-apple-system, "PingFang SC", "Noto Sans SC", sans-serif',
        color: 'white',
        overflow: 'hidden',
        width: 1920,
        height: 1080,
      }}
    >
      {/* Floating magnetic field line particles */}
      {Array.from({ length: 55 }).map((_, i) => {
        const x = (i * 307) % 1920;
        const y = (i * 173) % 1080;
        const drift = Math.sin(frame * 0.03 + i) * 6;
        const pulse = 0.06 + 0.05 * Math.sin(frame * 0.045 + i * 0.7);
        const colors = ['#0284c7', '#38bdf8', '#7dd3fc', '#bae6fd'];
        const color = colors[i % colors.length];
        const width = 8 + (i % 5) * 4;
        const height = 1.5 + (i % 3);
        const curve = Math.sin(frame * 0.02 + i * 0.3) * 4;
        return (
          <div key={`p-${i}`} style={{ position: 'absolute', left: x, top: y + drift }}>
            <svg width={width + 4} height={12} style={{ opacity: pulse }}>
              <path
                d={`M2,6 Q${width / 2},${6 + curve} ${width},6`}
                fill="none"
                stroke={color}
                strokeWidth={height}
                strokeLinecap="round"
              />
            </svg>
          </div>
        );
      })}

      {/* ===== Header ===== */}
      <div
        style={{
          position: 'absolute',
          top: 28,
          left: 44,
          right: 44,
          opacity: headerIn,
          transform: `translateY(${(1 - headerIn) * 20}px)`,
          display: 'flex',
          alignItems: 'center',
          gap: 20,
        }}
      >
        <div
          style={{
            padding: '6px 18px',
            background: '#0c4a6e',
            borderRadius: 8,
            fontSize: 24,
            fontWeight: 700,
            color: '#bae6fd',
            flexShrink: 0,
          }}
        >
          🧊 凝聚态物理
        </div>
        <h1
          style={{
            fontSize: 46,
            fontWeight: 900,
            margin: 0,
            color: '#e0f2fe',
            whiteSpace: 'nowrap',
          }}
        >
          {p.topic}
        </h1>
        <p
          style={{
            fontSize: 26,
            color: '#94a3b8',
            margin: 0,
            whiteSpace: 'nowrap',
          }}
        >
          {p.subtitle}
        </p>
      </div>

      {/* ===== LEFT COLUMN: Superconductor Types ===== */}
      <div style={{ position: 'absolute', top: 120, left: 36, width: 545 }}>
        <div
          style={{
            fontSize: 26,
            color: '#94a3b8',
            marginBottom: 10,
            letterSpacing: '0.08em',
          }}
        >
          超导体分类
        </div>
        {p.superconductorTypes.map((sc, i) => {
          const s = spring({
            frame: frame - (typeStart + i * 16),
            fps,
            config: { damping: 14, stiffness: 120 },
          });
          return (
            <div
              key={i}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                background: '#0a1e3d',
                borderRadius: 14,
                padding: '12px 16px',
                border: `1.5px solid ${sc.color}30`,
                marginBottom: 8,
                opacity: s,
                transform: `translateX(${(1 - s) * 30}px)`,
              }}
            >
              <div
                style={{
                  width: 52,
                  height: 52,
                  borderRadius: '50%',
                  background: `${sc.color}20`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 30,
                  flexShrink: 0,
                  boxShadow: `0 0 18px ${sc.color}30`,
                }}
              >
                {sc.icon}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                  <span style={{ fontSize: 24, fontWeight: 800, color: sc.color, flexShrink: 0 }}>
                    {sc.name}
                  </span>
                  <span style={{ fontSize: 22, color: '#94a3b8' }}>
                    {sc.characteristic}
                  </span>
                </div>
                <div style={{ fontSize: 22, color: '#64748b', marginTop: 2 }}>
                  {sc.example}
                </div>
              </div>
            </div>
          );
        })}

        {/* Key Phenomena */}
        <div style={{ marginTop: 16 }}>
          <div
            style={{
              fontSize: 24,
              color: '#94a3b8',
              marginBottom: 10,
              letterSpacing: '0.08em',
            }}
          >
            核心现象
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 8,
            }}
          >
            {p.keyPhenomena.map((kp, i) => {
              const s = spring({
                frame: frame - (phenomenonStart + i * 14),
                fps,
                config: { damping: 14, stiffness: 120 },
              });
              return (
                <div
                  key={i}
                  style={{
                    background: '#0a1e3d',
                    borderRadius: 12,
                    padding: '12px 10px',
                    border: '1px solid #0c4a6e40',
                    opacity: s,
                    transform: `scale(${0.8 + 0.2 * s})`,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                    <span style={{ fontSize: 24 }}>{kp.icon}</span>
                    <span style={{ fontSize: 22, fontWeight: 700, color: '#38bdf8' }}>{kp.name}</span>
                  </div>
                  <div style={{ fontSize: 20, color: '#64748b', marginTop: 2 }}>
                    {kp.description}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ===== CENTER: Meissner Effect SVG + Critical Temp Timeline ===== */}
      <div style={{ position: 'absolute', top: 120, left: 650, width: 780 }}>
        <div
          style={{
            fontSize: 26,
            color: '#94a3b8',
            marginBottom: 12,
            letterSpacing: '0.08em',
          }}
        >
          迈斯纳效应 · 磁场排斥
        </div>
        <svg width="760" height="460" viewBox="0 0 580 350">
          <defs>
            <linearGradient id="scBodyGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#0284c7" stopOpacity="0.6" />
              <stop offset="100%" stopColor="#0c4a6e" stopOpacity="0.8" />
            </linearGradient>
          </defs>

          {/* Superconductor body */}
          <g opacity={interpolate(frame, [svgStart, svgStart + 40], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' })}>
            <rect x="140" y="160" width="300" height="70" rx="10" fill="url(#scBodyGrad)" stroke="#38bdf8" strokeWidth="2" />
            <text x="290" y="200" fill="#e0f2fe" fontSize="20" fontWeight="700" textAnchor="middle">
              超导体 (T &lt; Tc)
            </text>
          </g>

          {/* Magnetic field lines - deflected around superconductor */}
          {[-100, -50, 0, 50, 100].map((offset, i) => {
            const cx = 290 + offset;
            const deflection = Math.max(0, 60 - Math.abs(offset) * 0.5);
            const lineOp = interpolate(
              frame,
              [svgStart + 20 + i * 8, svgStart + 50 + i * 8],
              [0, 0.6],
              { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
            );
            return (
              <g key={`field-${i}`} opacity={lineOp}>
                {/* Top approaching lines */}
                <path
                  d={`M${cx},10 L${cx},${80 - deflection * 0.3} Q${cx},${130 - deflection} ${cx + (offset > 0 ? deflection : -deflection)},155`}
                  fill="none"
                  stroke="#38bdf8"
                  strokeWidth="2"
                  strokeDasharray="6 3"
                />
                {/* Bottom exiting lines */}
                <path
                  d={`M${cx + (offset > 0 ? deflection : -deflection)},235 Q${cx},${250 + deflection} ${cx},270`}
                  fill="none"
                  stroke="#38bdf8"
                  strokeWidth="2"
                  strokeDasharray="6 3"
                />
                {/* Arrow at top */}
                <polygon
                  points={`${cx - 3},30 ${cx + 3},30 ${cx},20`}
                  fill="#38bdf8"
                  transform="rotate(180)"
                  style={{ transformOrigin: `${cx}px 25px` }}
                />
              </g>
            );
          })}

          {/* Flux exclusion zone label */}
          <g opacity={interpolate(frame, [svgStart + 50, svgStart + 75], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' })}>
            <text x="485" y="190" fill="#fbbf24" fontSize="17" fontWeight="700">
              B = 0
            </text>
            <text x="485" y="212" fill="#64748b" fontSize="16">
              内部磁场为零
            </text>
          </g>

          {/* Levitation indicator */}
          <g opacity={interpolate(frame, [svgStart + 60, svgStart + 85], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' })}>
            <rect x="230" y="120" width="120" height="30" rx="8" fill="#0c4a6e40" stroke="#7dd3fc40" strokeWidth="1" />
            <text x="290" y="142" fill="#7dd3fc" fontSize="18" fontWeight="700" textAnchor="middle">
              磁悬浮 ↕
            </text>
          </g>

          {/* B-field label */}
          <text x="30" y="20" fill="#94a3b8" fontSize="18" fontWeight="700">
            外磁场 B ↓
          </text>
        </svg>

        {/* Critical Temperature Timeline */}
        <div style={{ marginTop: -50 }}>
          <div
            style={{
              fontSize: 26,
              color: '#94a3b8',
              marginBottom: -10,
              letterSpacing: '0.08em',
            }}
          >
            临界温度突破史
          </div>
          <svg width="760" height="220" viewBox="0 0 760 220">
            {/* Timeline baseline */}
            <line x1="40" y1="100" x2="720" y2="100" stroke="#0c4a6e" strokeWidth="3" opacity="0.5" />

            {p.criticalTempData.map((ct, i) => {
              const xPos = 60 + i * 150;
              const s = spring({
                frame: frame - (svgStart + 80 + i * 14),
                fps,
                config: { damping: 14, stiffness: 120 },
              });
              return (
                <g key={i} opacity={s}>
                  <circle cx={xPos} cy={100} r="8" fill="#0284c7" stroke="#38bdf8" strokeWidth="2.5" />
                  <text x={xPos} y={70} fill="#38bdf8" fontSize="22" fontWeight="800" textAnchor="middle">
                    {ct.temp}
                  </text>
                  <text x={xPos} y={132} fill="#e0f2fe" fontSize="20" fontWeight="700" textAnchor="middle">
                    {ct.material}
                  </text>
                  <text x={xPos} y={160} fill="#64748b" fontSize="22" textAnchor="middle">
                    {ct.year}
                  </text>
                </g>
              );
            })}

            {/* Trend arrow */}
            <g opacity={meissnerDraw}>
              <line x1="60" y1="190" x2="700" y2="190" stroke="#22c55e40" strokeWidth="2" />
              <text x="720" y="194" fill="#22c55e" fontSize="18" fontWeight="700">
                →
              </text>
              <text x="380" y="218" fill="#22c55e60" fontSize="22" textAnchor="middle">
                临界温度持续突破
              </text>
            </g>
          </svg>
        </div>
      </div>

      {/* ===== RIGHT COLUMN: Applications + Facts ===== */}
      <div style={{ position: 'absolute', top: 120, right: 36, width: 380 }}>
        <div
          style={{
            fontSize: 26,
            color: '#94a3b8',
            marginBottom: 10,
            letterSpacing: '0.08em',
          }}
        >
          应用领域
        </div>
        {p.applications.map((app, i) => {
          const s = spring({
            frame: frame - (appStart + i * 16),
            fps,
            config: { damping: 14, stiffness: 120 },
          });
          return (
            <div
              key={i}
              style={{
                background: '#0a1e3d',
                borderRadius: 12,
                padding: '12px 16px',
                border: '1.5px solid #0c4a6e40',
                marginBottom: 8,
                opacity: s,
                transform: `translateY(${(1 - s) * 16}px)`,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 24 }}>{app.icon}</span>
                <span style={{ fontSize: 22, fontWeight: 800, color: '#7dd3fc' }}>
                  {app.name}
                </span>
                <span
                  style={{
                    fontSize: 17,
                    color: '#22c55e',
                    background: '#22c55e20',
                    padding: '2px 8px',
                    borderRadius: 4,
                    marginLeft: 'auto',
                  }}
                >
                  {app.status}
                </span>
              </div>
              <div style={{ fontSize: 20, color: '#94a3b8', marginTop: 6 }}>
                {app.detail}
              </div>
            </div>
          );
        })}

        {/* SC Facts */}
        <div style={{ marginTop: 20 }}>
          <div
            style={{
              fontSize: 24,
              color: '#94a3b8',
              marginBottom: 10,
              letterSpacing: '0.08em',
            }}
          >
            超导理论
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 8,
            }}
          >
            {p.scFacts.map((fact, i) => {
              const s = spring({
                frame: frame - (factStart + i * 12),
                fps,
                config: { damping: 14, stiffness: 130 },
              });
              return (
                <div
                  key={i}
                  style={{
                    background: '#0a1e3d',
                    borderRadius: 12,
                    padding: '14px 10px',
                    textAlign: 'center',
                    border: '1px solid #0c4a6e40',
                    opacity: s,
                    transform: `scale(${0.8 + 0.2 * s})`,
                  }}
                >
                  <div style={{ fontSize: 26, marginBottom: 4 }}>{fact.icon}</div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: '#38bdf8' }}>
                    {fact.label}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ===== Stats (bottom bar) ===== */}
      <div
        style={{
          position: 'absolute',
          bottom: 105,
          left: 36,
          right: 36,
          display: 'flex',
          gap: 20,
        }}
      >
        {p.stats.map((st, i) => {
          const stIn = spring({
            frame: frame - (statsStart + i * 14),
            fps,
            config: { damping: 14, stiffness: 100 },
          });
          return (
            <div
              key={i}
              style={{
                flex: 1,
                background: '#0a1e3d',
                borderRadius: 14,
                padding: '14px 10px',
                textAlign: 'center',
                border: '1px solid #0c4a6e40',
                opacity: stIn,
                transform: `scale(${0.8 + 0.2 * stIn})`,
              }}
            >
              <div style={{ fontSize: 30, fontWeight: 900, color: '#0284c7' }}>
                {st.value}
              </div>
              <div style={{ fontSize: 20, color: '#94a3b8', marginTop: 4 }}>
                {st.label}
              </div>
            </div>
          );
        })}
      </div>

      {/* ===== Bottom Quote ===== */}
      <div
        style={{
          position: 'absolute',
          bottom: 20,
          left: 36,
          right: 36,
          background: '#0a1e3d',
          borderRadius: 14,
          padding: '14px 22px',
          borderTop: '3px solid #0c4a6e',
          opacity: quoteOpacity,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 22 }}>🧊</span>
          <span style={{ fontSize: 22, color: '#e0f2fe', lineHeight: 1.6 }}>
            {p.bottomQuote}
          </span>
        </div>
      </div>

      {/* Progress bar */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: `${interpolate(frame, [0, 540], [0, 100], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          })}%`,
          height: 4,
          background: 'linear-gradient(90deg, #0c4a6e, #0284c7, #bae6fd)',
        }}
      />
    </AbsoluteFill>
  );
};
